import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Table, Tag, Button, Space, Typography, Tabs, message, Tooltip } from 'antd';
import { DashboardContainer } from 'components/Container/Container';
import { LoadingSpinnerView } from 'components/LoadingSpinner/LoadingSpinner';
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, EyeOutlined } from '@ant-design/icons';
import moment from 'moment';
import Api from 'api';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const PageHeader = styled.div`
  margin-bottom: 24px;

  h1 {
    margin-bottom: 8px;
  }
  .subtitle {
    color: #8c8c8c;
    font-size: 16px;
  }
`;

const StatsBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  .stat-card {
    padding: 20px;
    background: white;
    border-radius: 8px;
    border: 1px solid #f0f0f0;

    .stat-label {
      font-size: 14px;
      color: #8c8c8c;
      margin-bottom: 8px;
    }
    .stat-value {
      font-size: 32px;
      font-weight: 700;
    }
    &.approved .stat-value { color: #52c41a; }
    &.pending .stat-value { color: #faad14; }
    &.rejected .stat-value { color: #ff4d4f; }
    &.accuracy .stat-value { color: #1890ff; }
  }
`;

interface Props {}

const MyErrorReports: React.FC<Props> = () => {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState<any[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0,
    accuracy: 0
  });
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchReports('all');
  }, []);

  const fetchReports = async (status: string) => {
    try {
      setLoading(true);
      const { data } = await Api.get(`/species-errors/my-reports?status=${status}&limit=100`);

      setReports(data.data.reports || []);

      // Calculate stats from all reports
      if (status === 'all') {
        const all = data.data.reports || [];
        const approved = all.filter((r: any) => r.status === 'approved' || r.status === 'fixed');
        const pending = all.filter((r: any) => r.status === 'pending');
        const rejected = all.filter((r: any) => r.status === 'rejected');
        const accuracy = all.length > 0
          ? Math.round((approved.length / all.length) * 100)
          : 0;

        setStats({
          total: all.length,
          approved: approved.length,
          pending: pending.length,
          rejected: rejected.length,
          accuracy
        });
      }

      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching reports:', error);
      message.error('Failed to fetch error reports');
      setLoading(false);
    }
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    fetchReports(key);
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'approved':
      case 'fixed':
        return <Tag color="success" icon={<CheckCircleOutlined />}>Approved</Tag>;
      case 'rejected':
        return <Tag color="error" icon={<CloseCircleOutlined />}>Rejected</Tag>;
      case 'pending':
      default:
        return <Tag color="warning" icon={<ClockCircleOutlined />}>Pending</Tag>;
    }
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => getStatusTag(status)
    },
    {
      title: 'Species',
      key: 'species',
      render: (record: any) => (
        <div>
          <div><Text strong>{record.species?.common_name || record.species?.scientific_name}</Text></div>
          {record.species?.common_name && record.species?.scientific_name && (
            <div><Text type="secondary" italic style={{ fontSize: 12 }}>{record.species.scientific_name}</Text></div>
          )}
        </div>
      )
    },
    {
      title: 'Error Type',
      dataIndex: 'error_type',
      key: 'error_type',
      width: 180,
      render: (type: string) => (
        <Tag>{type.replace(/_/g, ' ').toUpperCase()}</Tag>
      )
    },
    {
      title: 'Field',
      dataIndex: 'field_name',
      key: 'field_name',
      width: 120
    },
    {
      title: 'Suggested Fix',
      dataIndex: 'suggested_correction',
      key: 'suggested_correction',
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      )
    },
    {
      title: 'Submitted',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => moment(date).format('MMM D, YYYY')
    },
    {
      title: 'Points',
      dataIndex: 'points_awarded',
      key: 'points_awarded',
      width: 80,
      align: 'center' as 'center',
      render: (points: number) => points > 0 ? <Tag color="gold">+{points}</Tag> : '-'
    }
  ];

  if (loading) {
    return <LoadingSpinnerView size={80} />;
  }

  return (
    <DashboardContainer>
      <PageHeader>
        <Title level={1}>My Error Reports</Title>
        <div className="subtitle">
          Track your submitted error corrections and contributions to data quality
        </div>
      </PageHeader>

      <StatsBar>
        <div className="stat-card">
          <div className="stat-label">Total Reports</div>
          <div className="stat-value">{stats.total}</div>
        </div>
        <div className="stat-card approved">
          <div className="stat-label">Approved</div>
          <div className="stat-value">{stats.approved}</div>
        </div>
        <div className="stat-card pending">
          <div className="stat-label">Pending Review</div>
          <div className="stat-value">{stats.pending}</div>
        </div>
        <div className="stat-card accuracy">
          <div className="stat-label">Accuracy Rate</div>
          <div className="stat-value">{stats.accuracy}%</div>
        </div>
      </StatsBar>

      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        <TabPane tab="All Reports" key="all" />
        <TabPane tab="Pending" key="pending" />
        <TabPane tab="Approved" key="approved" />
        <TabPane tab="Rejected" key="rejected" />
      </Tabs>

      <Table
        columns={columns}
        dataSource={reports}
        rowKey="_id"
        pagination={{ pageSize: 20 }}
        expandable={{
          expandedRowRender: (record) => (
            <div style={{ padding: '16px', background: '#fafafa' }}>
              <div style={{ marginBottom: 12 }}>
                <Text strong>Description:</Text>
                <div>{record.description}</div>
              </div>
              {record.current_value && (
                <div style={{ marginBottom: 12 }}>
                  <Text strong>Current Value:</Text> {record.current_value}
                </div>
              )}
              {record.evidence_url && (
                <div style={{ marginBottom: 12 }}>
                  <Text strong>Evidence:</Text>{' '}
                  <a href={record.evidence_url} target="_blank" rel="noopener noreferrer">
                    {record.evidence_url}
                  </a>
                </div>
              )}
              {record.review_notes && (
                <div style={{ marginBottom: 12 }}>
                  <Text strong>Review Notes:</Text>
                  <div style={{ padding: 12, background: '#fff', borderRadius: 4, marginTop: 4 }}>
                    {record.review_notes}
                  </div>
                </div>
              )}
              {record.reviewed_at && (
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Reviewed {moment(record.reviewed_at).fromNow()} by {record.reviewed_by?.name || 'Admin'}
                  </Text>
                </div>
              )}
            </div>
          ),
          expandIcon: ({ expanded, onExpand, record }) =>
            expanded ? (
              <EyeOutlined onClick={(e) => onExpand(record, e)} />
            ) : (
              <EyeOutlined onClick={(e) => onExpand(record, e)} />
            )
        }}
      />
    </DashboardContainer>
  );
};

export default MyErrorReports;
